import { useState } from 'react';

import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';

interface UsePendingRoleSelectionReturn<TKey> {
  /** Records entities that were just added and still need a role. */
  markPending: (keys: readonly TKey[]) => void;
  /** Clears entities whose role was set, or that were removed outright. */
  resolvePending: (keys: readonly TKey[]) => void;
  isBlocked: boolean;
  proceed: () => void;
  reset: () => void;
}

/** Tracks entities added during this visit whose role is still unspecified, and
 * blocks navigation while any remain.
 *
 * Keys are identities (user id, group id) rather than a bare count: resolving the
 * same entity twice, or resolving one that was never added here, is a no-op
 * instead of silently unblocking on someone else's behalf. */
export function usePendingRoleSelection<TKey extends string | number>(): UsePendingRoleSelectionReturn<TKey> {
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<TKey>>(() => new Set<TKey>());

  const markPending = (keys: readonly TKey[]) =>
    setPendingKeys((prev) => {
      if (keys.every((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      keys.forEach((key) => next.add(key));
      return next;
    });

  const resolvePending = (keys: readonly TKey[]) =>
    setPendingKeys((prev) => {
      if (!keys.some((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      keys.forEach((key) => next.delete(key));
      return next;
    });

  const { isBlocked, proceed, reset } = useNavigationBlocker(pendingKeys.size > 0);

  return { markPending, resolvePending, isBlocked, proceed, reset };
}
